import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Star, Shield, Zap, Smartphone, Wifi, Headphones } from 'lucide-react';
import ComparisonPortal from './ComparisonPortal';
import { trackPartnerClick } from '../utils/analytics';

interface CategoryContentProps {
  category: string;
}

const SmartphonesContent: React.FC = () => {
  return (
    <div>
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Smartphones kaufen - Die besten Angebote 2026</h2>
          <p className="text-gray-600 text-lg mb-6">
            Bei Easy Vertrag finden Sie die neuesten Smartphones zu den besten Preisen - ob iPhone, Samsung Galaxy, Google Pixel oder andere Top-Modelle. Vergleichen Sie hunderte Angebote von Telekom, Vodafone, O2 und Congstar und sichern Sie sich Ihr Wunsch-Smartphone mit dem passenden Vertrag.
          </p>
          <p className="text-gray-600 mb-8">
            Egal ob Sie ein Flaggschiff-Smartphone oder ein günstiges Einsteiger-Modell suchen: Wir helfen Ihnen, das beste Angebot zu finden. Mit unseren exklusiven Affiliate-Deals können Sie bis zu 50% gegenüber dem Listenpreis sparen.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-blue-50 p-6 rounded-xl">
              <Smartphone className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="font-bold text-gray-800 mb-2">Apple iPhone</h3>
              <p className="text-gray-600 text-sm">iPhone 16, iPhone 16 Pro, iPhone 16 Pro Max - alle aktuellen Modelle mit Vertrag oder ohne.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <Smartphone className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="font-bold text-gray-800 mb-2">Samsung Galaxy</h3>
              <p className="text-gray-600 text-sm">Samsung Galaxy S25, Galaxy A-Serie und mehr - Qualität zu jedem Preis.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <Smartphone className="w-8 h-8 text-gray-600 mb-3" />
              <h3 className="font-bold text-gray-800 mb-2">Weitere Hersteller</h3>
              <p className="text-gray-600 text-sm">Google Pixel, Xiaomi, OPPO und viele weitere Marken im Vergleich.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Smartphone mit Vertrag - Vorteile im Überblick</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              'Kein hoher Einmalbetrag - monatliche Raten',
              'Sofortiger Internetzugang im Netz Ihrer Wahl',
              'Oft inklusive kostenloser Lieferung',
              'Flexibel kündbar ab 12 Monaten Laufzeit',
              'Garantie und Versicherungsoptionen verfügbar',
              'Aufrüstung auf neues Gerät nach Vertragsende',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Häufige Fragen zum Smartphone-Kauf</h2>
          <div className="space-y-4">
            {[
              {
                q: 'Welches Smartphone ist das beste 2026?',
                a: 'Das hängt von Ihren Bedürfnissen ab. Für Premium-Nutzer empfehlen wir das iPhone 16 Pro Max oder Samsung Galaxy S25 Ultra. Für ein gutes Preis-Leistungs-Verhältnis sind das Google Pixel 9 oder Samsung Galaxy A55 ausgezeichnete Wahl.',
              },
              {
                q: 'Wie viel kostet ein Smartphone mit Vertrag?',
                a: 'Smartphones mit Vertrag beginnen bei ca. 20-30 €/Monat für günstige Modelle. Premium-Smartphones mit Datentarif liegen oft zwischen 50-80 €/Monat. Die Hardwarekosten werden auf die Laufzeit aufgeteilt.',
              },
              {
                q: 'Lohnt sich ein Smartphone ohne Vertrag?',
                a: 'Ein Smartphone ohne Vertrag (SIM-free) lohnt sich, wenn Sie bereits einen günstigen Datentarif haben oder häufig den Anbieter wechseln möchten. Sie zahlen einmalig den vollen Gerätepreis, sind aber langfristig flexibler.',
              },
            ].map((item, i) => (
              <details key={i} className="bg-gray-50 rounded-lg p-5 group">
                <summary className="font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-gray-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-12 bg-blue-600 text-white">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-2xl font-bold mb-4">Jetzt Smartphone-Angebote vergleichen</h2>
        <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
          Nutzen Sie unsere Partnerportale, um die besten Smartphone-Angebote aller Netzbetreiber direkt zu vergleichen.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="https://ndirect.ppro.de/click/huGw" rel="nofollow sponsored" target="_blank"
            onClick={() => trackPartnerClick('Smartphones vergleichen', 'category_smartphones')}
            className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors">
            Alle Smartphones vergleichen <ArrowRight className="ml-2 w-4 h-4" />
          </a>
          <Link to="/produkte/handytarif"
            className="inline-flex items-center px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
            Handytarife ansehen
          </Link>
        </div>
      </div>
    </section>
  </div>
  );
};

const HandytarifContent: React.FC = () => {
  return (
  <div>
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Handytarife vergleichen & günstig abschließen</h2>
          <p className="text-gray-600 text-lg mb-6">
            Finden Sie den günstigsten Handytarif für Ihre Bedürfnisse. Ob Einsteiger, Vieltelefonierer oder Datenflatrate - Easy Vertrag vergleicht alle Tarife von Telekom, Vodafone, O2, Congstar, Blau und vielen weiteren Anbietern.
          </p>
          <p className="text-gray-600 mb-8">
            Mit unserem Tarifvergleich sparen Sie bares Geld und finden in wenigen Minuten den perfekten Mobilfunktarif - ohne versteckte Kosten und mit transparenter Preisübersicht.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {[
              { title: 'Telekom Tarife', desc: 'Bestes Netz in Deutschland - D1-Netz mit 5G', color: 'bg-pink-50', iconColor: 'text-pink-600' },
              { title: 'Vodafone Tarife', desc: 'D2-Netz mit starker Netzabdeckung und 5G', color: 'bg-red-50', iconColor: 'text-red-600' },
              { title: 'O2 Tarife', desc: 'E-Netz - günstige Tarife mit guter Abdeckung', color: 'bg-blue-50', iconColor: 'text-blue-600' },
            ].map((item, i) => (
              <div key={i} className={`${item.color} p-5 rounded-xl`}>
                <Zap className={`w-7 h-7 ${item.iconColor} mb-3`} />
                <h3 className="font-bold text-gray-800 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Was ist beim Handytarif-Vergleich wichtig?</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: <Wifi className="w-6 h-6 text-blue-600" />,
                title: 'Datenvolumen',
                desc: 'Wie viel GB brauchen Sie monatlich? Für Streaming empfehlen wir mindestens 10 GB, für Vielnutzer 30+ GB oder eine echte Flatrate.',
              },
              {
                icon: <Star className="w-6 h-6 text-yellow-500" />,
                title: 'Netzqualität',
                desc: 'Das Telekom-Netz (D1) ist Testsieger in Deutschland. Vodafone (D2) und O2 bieten ebenfalls sehr gute Abdeckung.',
              },
              {
                icon: <Shield className="w-6 h-6 text-green-600" />,
                title: 'Vertragslaufzeit',
                desc: 'Tarife mit 24 Monaten Laufzeit sind günstiger. Monatlich kündbare Tarife bieten maximale Flexibilität.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="mb-3">{item.icon}</div>
                <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <ComparisonPortal />
      </div>
    </section>

    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Häufige Fragen zu Handytarifen</h2>
          <div className="space-y-4">
            {[
              {
                q: 'Was ist der günstigste Handytarif in Deutschland?',
                a: 'Günstige Handytarife beginnen ab ca. 5-10 €/Monat bei Anbietern wie Blau, Congstar oder Freenet. Diese Tarife laufen oft im O2- oder Telekom-Netz. Bei Easy Vertrag finden Sie aktuelle Angebote aller Anbieter im Vergleich.',
              },
              {
                q: 'Was ist der Unterschied zwischen Prepaid und Postpaid?',
                a: 'Bei Prepaid zahlen Sie im Voraus und haben keine Bindung. Bei Postpaid (Vertragstarif) zahlen Sie monatlich per Rechnung, bekommen dafür oft mehr Leistung zu günstigeren Konditionen.',
              },
              {
                q: 'Kann ich meinen Tarif vorzeitig kündigen?',
                a: 'Bei einem 24-Monats-Vertrag können Sie nicht vorzeitig kündigen, außer es gibt einen wichtigen Grund. Nach Ablauf der Mindestlaufzeit gilt eine Kündigungsfrist von meist einem Monat.',
              },
              {
                q: 'Wie wechsle ich meinen Handytarif?',
                a: 'Um zu einem neuen Anbieter zu wechseln, kündigen Sie Ihren alten Vertrag und beantragen bei Ihrem neuen Anbieter die Rufnummernmitnahme (Portierung). Der Prozess dauert in der Regel 1-3 Werktage.',
              },
            ].map((item, i) => (
              <details key={i} className="bg-white rounded-lg p-5 shadow-sm group">
                <summary className="font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-gray-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>

  </div>
  );
};

const GeraeteContent: React.FC = () => {
  return (
  <div>
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Tablets, Smartwatches & Zubehör</h2>
          <p className="text-gray-600 text-lg mb-6">
            Entdecken Sie unser umfassendes Sortiment an Tablets, Smartwatches, Bluetooth-Kopfhörern und weiterem Technik-Zubehör. Bei Easy Vertrag finden Sie die neuesten Modelle von Apple, Samsung, Huawei und mehr zu den besten Preisen.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-blue-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                <Wifi className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Tablets</h3>
              <p className="text-gray-600 text-sm mb-3">iPad, Samsung Galaxy Tab, Amazon Fire und viele mehr. Mit oder ohne SIM-Karte und Datentarif.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Apple iPad & iPad Pro</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Samsung Galaxy Tab S</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Amazon Fire HD</li>
              </ul>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <Star className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Smartwatches</h3>
              <p className="text-gray-600 text-sm mb-3">Fitness-Tracker und Smartwatches für jeden Bedarf - von der Apple Watch bis zur Samsung Galaxy Watch.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Apple Watch Series 10</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Samsung Galaxy Watch 7</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Garmin & Fitbit</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Headphones className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Bluetooth-Kopfhörer</h3>
              <p className="text-gray-600 text-sm mb-3">True-Wireless-Kopfhörer und Over-Ear-Headsets mit aktiver Geräuschunterdrückung.</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Apple AirPods Pro</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Samsung Galaxy Buds</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Sony & Bose</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Warum Geräte bei Easy Vertrag kaufen?</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              'Günstigste Preise durch Anbietervergleich',
              'Originalgeräte mit vollständiger Herstellergarantie',
              'Schnelle Lieferung in 2-3 Werktagen',
              'Kauf auf Rechnung, Ratenkauf oder PayPal',
              'Kostenloser Versand ab 50 € Bestellwert',
              'Einfaches Widerrufsrecht innerhalb von 14 Tagen',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="https://ndirect.ppro.de/click/huGw" rel="nofollow sponsored" target="_blank"
              onClick={() => trackPartnerClick('Geraete vergleichen', 'category_geraete')}
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
              Alle Geräte vergleichen <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>

  </div>
  );
};

const MobilfunkContent: React.FC = () => {
  return (
  <div>
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Mobilfunktarife vergleichen - Alle Anbieter im Überblick</h2>
          <p className="text-gray-600 text-lg mb-6">
            Vergleichen Sie alle Mobilfunktarife von Telekom, Vodafone, O2, Congstar, Blau und vielen weiteren Anbietern. Easy Vertrag zeigt Ihnen transparent alle Kosten und hilft Ihnen, den günstigsten Tarif für Ihre Bedürfnisse zu finden.
          </p>
        </div>
      </div>
    </section>

    <section className="py-8 bg-gray-50">
      <div className="container mx-auto px-4">
        <ComparisonPortal />
      </div>
    </section>

    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-8">Die wichtigsten Mobilfunknetze in Deutschland</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {[
              {
                name: 'Telekom (D1-Netz)',
                coverage: '99,9% Bevölkerungsabdeckung',
                tech: '5G, LTE bis 300 Mbit/s',
                rating: 'Testsieger Netzqualität',
                color: 'border-l-pink-500',
              },
              {
                name: 'Vodafone (D2-Netz)',
                coverage: '99,7% Bevölkerungsabdeckung',
                tech: '5G, LTE bis 300 Mbit/s',
                rating: 'Sehr gut bewertet',
                color: 'border-l-red-500',
              },
              {
                name: 'O2 / Telefónica (E-Netz)',
                coverage: '99,3% Bevölkerungsabdeckung',
                tech: '5G, LTE bis 225 Mbit/s',
                rating: 'Günstigster Preis',
                color: 'border-l-blue-500',
              },
              {
                rating: 'Günstige Einstiegspreise',
                color: 'border-l-cyan-500',
              },
            ].map((net, i) => (
              <div key={i} className={`bg-gray-50 border-l-4 ${net.color} p-5 rounded-r-xl`}>
                <h3 className="font-bold text-gray-800 mb-2">{net.name}</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />{net.coverage}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />{net.tech}</li>
                  <li className="flex items-center gap-2"><Star className="w-4 h-4 text-yellow-500" />{net.rating}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

  </div>
  );
};

const CategoryContent: React.FC<CategoryContentProps> = ({ category }) => {
  switch (category) {
    case 'smartphones':
      return <SmartphonesContent />;
    case 'handytarif':
      return <HandytarifContent />;
    case 'geraete':
      return <GeraeteContent />;
    case 'mobilfunk':
      return <MobilfunkContent />;
    default:
      return (
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <p className="text-gray-600 text-lg">In dieser Kategorie sind demnächst neue Angebote verfügbar.</p>
            <Link to="/" className="mt-6 inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Zur Startseite
            </Link>
          </div>
        </section>
      );
  }
};

export default CategoryContent;
